import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Task, TaskFormData, TaskCategory } from "@/types/task";
import { useMemo } from "react";

export const useTasks = () => {
  const queryClient = useQueryClient();

  const { data: employeesData = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const { data, error } = await supabase.from("employees").select("id, name");
      if (error) throw error;
      return data;
    },
  });

  const employeesMap = useMemo(() => 
    new Map(employeesData.map(e => [e.id, e.name]))
  , [employeesData]);

  const { data: rawTasks = [], isLoading: tasksIsLoading, error } = useQuery({
    queryKey: ["tasks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tasks")
        .select(`
          *,
          task_categories(name_ar, color),
          clients(name_ar),
          companies(name_ar),
          suppliers(name_ar)
        `)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as Omit<Task, 'employees' | 'assigned_employee'>[];
    },
  });

  const tasks: Task[] = useMemo(() => {
    return rawTasks.map((task) => ({
      ...task,
      employees: task.created_by ? { name: employeesMap.get(task.created_by) || "مستخدم محذوف" } : undefined,
      assigned_employee: task.assigned_to ? { name: employeesMap.get(task.assigned_to) || "غير محدد" } : undefined,
    }));
  }, [rawTasks, employeesMap]);
  
  const isLoading = tasksIsLoading;

  const createTaskMutation = useMutation({
    mutationFn: async (taskData: TaskFormData) => {
      // Get current user if available
      const { data: { user } } = await supabase.auth.getUser();
      
      const taskToCreate = {
        ...taskData,
        created_by: user?.id,
        status: taskData.status || 'pending'
      };
      
      console.log('Creating task with data:', taskToCreate);
      
      const { data, error } = await supabase
        .from("tasks")
        .insert([taskToCreate])
        .select()
        .single();
      
      if (error) {
        console.error('Task creation error:', error);
        throw error;
      }
      console.log('Task created successfully:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<TaskFormData> }) => {
      console.log('Updating task:', id, 'with data:', updates);
      
      const { data, error } = await supabase
        .from("tasks")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      
      if (error) {
        console.error('Task update error:', error);
        throw error;
      }
      console.log('Task updated successfully:', data);
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      // Optimistically update the specific task query if one exists
      queryClient.setQueryData(['tasks', variables.id], data);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      console.log('Deleting task:', taskId);
      const { error } = await supabase
        .from("tasks")
        .delete()
        .eq("id", taskId);
      
      if (error) {
        console.error('Task deletion error:', error);
        throw error;
      }
      console.log('Task deleted successfully');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const createTask = async (taskData: TaskFormData): Promise<boolean> => {
    try {
      await createTaskMutation.mutateAsync(taskData);
      return true;
    } catch (error) {
      console.error("Error creating task:", error);
      return false;
    }
  };

  const updateTask = async (id: string, updates: Partial<TaskFormData>): Promise<boolean> => {
    try {
      await updateTaskMutation.mutateAsync({ id, updates });
      return true;
    } catch (error) {
      console.error("Error updating task:", error);
      return false;
    }
  };

  const deleteTask = (taskId: string) => {
    deleteTaskMutation.mutate(taskId);
  };

  return {
    tasks,
    isLoading,
    error,
    createTask,
    updateTask,
    deleteTask,
    isCreating: createTaskMutation.isPending,
    isUpdating: updateTaskMutation.isPending,
    isDeleting: deleteTaskMutation.isPending,
  };
};

export const useTaskCategories = () => {
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["task-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("task_categories")
        .select("*")
        .order("name_ar");
      
      if (error) throw error;
      return data as TaskCategory[];
    },
  });

  return { categories, isLoading };
};

export const useAirlines = () => {
  const { data: airlines = [], isLoading, error } = useQuery({
    queryKey: ["airlines"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("airlines")
        .select("id, name_ar, code")
        .order("name_ar");
      
      if (error) {
        console.error('Error fetching airlines:', error);
        throw error;
      }
      return data;
    },
  });

  return { airlines, isLoading, error };
};

export const useVehicleTypes = () => {
  const { data: vehicleTypes = [], isLoading, error } = useQuery({
    queryKey: ["vehicle_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicle_types")
        .select("id, name_ar")
        .order("sort_order");
      
      if (error) {
        console.error('Error fetching vehicle types:', error);
        throw error;
      }
      return data;
    },
  });

  return { vehicleTypes, isLoading, error };
};
